/**
 * AIAdvisor.js — Chat-style AI contingency advisor interface with risk score.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useContingencyAdvisor } from '../../agents/contingencyAgent';

const QUICK_PROMPTS = {
  buyer: [
    'What should I do first?',
    'Is this a fair repair request?',
    'What is my risk level?',
    "Should I walk away?",
    'Can I request more time?',
  ],
  seller: [
    'What repairs are reasonable to agree to?',
    'Is the appraisal gap typical?',
    'How do I respond to the buyer request?',
    'Will agreeing to repairs hurt my profit?',
  ],
};

export default function AIAdvisor({ contingency, role = 'buyer' }) {
  const { messages, riskScore, loading, error, ask, loadRiskScore } = useContingencyAdvisor(contingency?.id);
  const [input, setInput] = useState('');
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (contingency?.id) loadRiskScore();
  }, [contingency?.id, loadRiskScore]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const q = input.trim();
    if (!q) return;
    setInput('');
    await ask(q, role);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const riskLevel = riskScore == null ? null : riskScore < 35 ? 'low' : riskScore < 65 ? 'medium' : 'high';
  const riskLabel = { low: 'Low Risk', medium: 'Medium Risk', high: 'High Risk' };

  return (
    <div>
      {/* Risk score widget */}
      {riskScore != null && (
        <div className="ch-card" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
          <div className="ch-risk-gauge" style={{ flex: 1 }}>
            <div className={`ch-risk-score ${riskLevel}`}>{riskScore}</div>
            <div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, fontSize: '0.95rem', color: 'var(--text-primary)' }}>{riskLabel[riskLevel]}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                {riskLevel === 'low' && 'This contingency is on track.'}
                {riskLevel === 'medium' && 'Some attention needed — act before deadline.'}
                {riskLevel === 'high' && 'Urgent action required — deal may be at risk.'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick prompts */}
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        {(QUICK_PROMPTS[role] || QUICK_PROMPTS.buyer).map(prompt => (
          <button
            key={prompt}
            className="ch-btn ch-btn-ghost"
            style={{ fontSize: '0.78rem', padding: '0.3rem 0.65rem' }}
            onClick={() => ask(prompt, role)}
            disabled={loading}
          >
            {prompt}
          </button>
        ))}
      </div>

      {/* Chat window */}
      <div className="ch-card" style={{ padding: '0' }}>
        <div className="ch-advisor-chat" style={{ padding: '1rem' }}>
          {messages.length === 0 && (
            <div className="ch-empty" style={{ margin: 0 }}>
              Ask me anything about this contingency — I'll explain your options in plain English.
            </div>
          )}
          {messages.map((msg, idx) => (
            <div key={idx} className={`ch-msg ${msg.role}`}>
              {msg.content}
              {msg.disclaimer && (
                <div className="ch-msg-disclaimer">{msg.disclaimer}</div>
              )}
            </div>
          ))}
          {loading && (
            <div className="ch-msg assistant" style={{ color: '#a0aec0' }}>
              Thinking…
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Input row */}
        <div className="ch-advisor-input-row" style={{ padding: '0.75rem 1rem', borderTop: '1px solid var(--border)' }}>
          <input
            className="ch-advisor-input"
            type="text"
            placeholder="Ask your AI advisor…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
          <button
            className="ch-btn ch-btn-primary"
            onClick={handleSend}
            disabled={loading || !input.trim()}
            style={{ flexShrink: 0 }}
          >
            {loading ? '…' : '→'}
          </button>
        </div>
      </div>

      {error && <div className="ch-error" style={{ marginTop: '0.5rem' }}>{error}</div>}
    </div>
  );
}
